/**
 * ScriptEditorPage — Tier 4.3 Web Script Editor Admin UI
 *
 * Allows admins to:
 * - List all tenant scripts
 * - Create new scripts with code editor
 * - Edit script source code
 * - Execute scripts with test input
 * - View execution history and logs
 * - Enable/disable scripts
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Code,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  Play,
  Pause,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Terminal,
  FileCode,
  History,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ────────────────────────────────────────

interface Script {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  scriptType: string;
  language: string;
  sourceCode: string;
  config: Record<string, unknown>;
  maxDurationMs: number;
  maxMemoryMb: number;
  allowedApis: string[];
  enabled: boolean;
  version: number;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ExecutionResult {
  executionId: string;
  status: string;
  output: unknown;
  logs: string[];
  error?: string;
  durationMs: number;
}

interface ExecutionRow {
  id: string;
  status: string;
  input: Record<string, unknown>;
  output: unknown;
  error: string | null;
  logs: string[];
  duration_ms: number;
  triggered_by: string;
  created_at: string;
}

type ScriptType = 'manual' | 'webhook_handler' | 'scheduled' | 'entity_hook' | 'workflow_step';

// ─── Component ───────────────────────────────────

export default function ScriptEditorPage() {
  const { t } = useTranslation();
  const [scripts, setScripts] = useState<Script[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingCode, setEditingCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [showExecute, setShowExecute] = useState(false);
  const [executeInput, setExecuteInput] = useState('{}');
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);
  const [executions, setExecutions] = useState<ExecutionRow[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Create form
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newType, setNewType] = useState<ScriptType>('manual');
  const [newCode, setNewCode] = useState(DEFAULT_SCRIPT);

  const fetchScripts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<Script[]>('/scripts');
      setScripts(data);
    } catch { setScripts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchScripts(); }, [fetchScripts]);

  const selected = scripts.find((s) => s.id === selectedId);

  const handleSelect = (s: Script) => {
    setSelectedId(s.id);
    setEditingCode(s.sourceCode);
    setExecutionResult(null);
    setShowHistory(false);
    setShowExecute(false);
  };

  const handleCreate = async () => {
    if (!newName) return;
    setSaving(true);
    try {
      await api.post('/scripts', {
        name: newName,
        description: newDescription || undefined,
        scriptType: newType,
        sourceCode: newCode,
      });
      setShowCreate(false);
      setNewName('');
      setNewDescription('');
      setNewCode(DEFAULT_SCRIPT);
      fetchScripts();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create script');
    } finally { setSaving(false); }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await api.patch(`/scripts/${selectedId}`, { sourceCode: editingCode });
      fetchScripts();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this script?')) return;
    try {
      await api.delete(`/scripts/${id}`);
      if (selectedId === id) setSelectedId(null);
      fetchScripts();
    } catch { /* ignore */ }
  };

  const handleToggle = async (s: Script) => {
    try {
      await api.patch(`/scripts/${s.id}`, { enabled: !s.enabled });
      fetchScripts();
    } catch { /* ignore */ }
  };

  const handleExecute = async () => {
    if (!selectedId) return;
    setExecuting(true);
    setExecutionResult(null);
    try {
      const input = JSON.parse(executeInput);
      const result = await api.post<ExecutionResult>(`/scripts/${selectedId}/execute`, { input });
      setExecutionResult(result);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Execution failed');
    } finally { setExecuting(false); }
  };

  const loadHistory = async () => {
    if (!selectedId) return;
    setShowHistory(true);
    try {
      const rows = await api.get<ExecutionRow[]>(`/scripts/${selectedId}/executions?limit=20`);
      setExecutions(rows);
    } catch { setExecutions([]); }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Code className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">Script Editor</h1>
            <p className="text-sm text-muted-foreground">
              Create and run tenant-scoped JavaScript scripts
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchScripts}>
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
            <Plus className="mr-1 h-4 w-4" /> New Script
          </Button>
        </div>
      </div>

      {/* Create Form */}
      {showCreate && (
        <Card className="border-dashed border-primary/40 bg-primary/5">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium">Script Name</label>
                <Input className="mt-1" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="cleanup-old-jobs" />
              </div>
              <div>
                <label className="text-xs font-medium">Type</label>
                <select
                  className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as ScriptType)}
                >
                  <option value="manual">Manual</option>
                  <option value="webhook_handler">Webhook Handler</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="entity_hook">Entity Hook</option>
                  <option value="workflow_step">Workflow Step</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Description</label>
                <Input className="mt-1" value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="Optional description" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">Source Code</label>
              <textarea
                className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                rows={8}
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreate} disabled={saving || !newName}>
                {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                Create Script
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-6">
          {/* Script List */}
          <div className="space-y-2">
            <h2 className="text-sm font-medium text-muted-foreground">Scripts ({scripts.length})</h2>
            {scripts.map((s) => (
              <div
                key={s.id}
                className={cn(
                  'cursor-pointer rounded-md border px-3 py-2 transition-colors hover:bg-muted/50',
                  selectedId === s.id && 'border-primary bg-primary/5',
                )}
                onClick={() => handleSelect(s)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{s.name}</span>
                  <div className="flex items-center gap-1">
                    <Badge variant={s.enabled ? 'default' : 'secondary'} className="text-[10px]">
                      {s.enabled ? 'ON' : 'OFF'}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">{s.scriptType}</Badge>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  v{s.version} • {s.language}
                  {s.lastRunStatus && (
                    <span className={cn('ml-2', s.lastRunStatus === 'success' ? 'text-green-600' : 'text-red-600')}>
                      Last: {s.lastRunStatus}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {scripts.length === 0 && (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No scripts. Create one to get started.
              </p>
            )}
          </div>

          {/* Script Detail / Editor */}
          <div className="col-span-2 space-y-4">
            {selected ? (
              <>
                {/* Actions bar */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">{selected.name}</h2>
                    <p className="text-xs text-muted-foreground">
                      {selected.description || 'No description'} • v{selected.version} • {selected.language}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleToggle(selected)}>
                      {selected.enabled ? <Pause className="mr-1 h-4 w-4" /> : <Play className="mr-1 h-4 w-4" />}
                      {selected.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setShowExecute(!showExecute); setExecutionResult(null); }}>
                      <Terminal className="mr-1 h-4 w-4" /> Execute
                    </Button>
                    <Button variant="outline" size="sm" onClick={loadHistory}>
                      <History className="mr-1 h-4 w-4" /> History
                    </Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(selected.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Code Editor */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileCode className="h-4 w-4" /> Source Code
                    </CardTitle>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={saving || editingCode === selected.sourceCode}
                    >
                      {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                      Save
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <textarea
                      className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono leading-relaxed"
                      rows={18}
                      value={editingCode}
                      onChange={(e) => setEditingCode(e.target.value)}
                      spellCheck={false}
                    />
                    {editingCode !== selected.sourceCode && (
                      <p className="mt-1 text-xs text-yellow-600">Unsaved changes</p>
                    )}
                  </CardContent>
                </Card>

                {/* Execute Panel */}
                {showExecute && (
                  <Card className="border-dashed border-primary/40">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-base">Execute Script</CardTitle>
                      <Button size="sm" onClick={handleExecute} disabled={executing}>
                        {executing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
                        Run
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <label className="text-xs font-medium">Input (JSON)</label>
                        <textarea
                          className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                          rows={3}
                          value={executeInput}
                          onChange={(e) => setExecuteInput(e.target.value)}
                          placeholder='{"key": "value"}'
                        />
                      </div>

                      {executionResult && (
                        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {executionResult.status === 'success' ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-500" />
                              )}
                              <Badge variant={executionResult.status === 'success' ? 'default' : 'destructive'}>
                                {executionResult.status}
                              </Badge>
                            </div>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {executionResult.durationMs}ms
                            </span>
                          </div>

                          {executionResult.error && (
                            <div className="rounded bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-700 dark:text-red-400">
                              {executionResult.error}
                            </div>
                          )}

                          {executionResult.logs.length > 0 && (
                            <div>
                              <p className="text-xs font-medium mb-1">Console Output:</p>
                              <pre className="rounded bg-black/90 text-green-400 px-3 py-2 text-xs overflow-auto max-h-40">
                                {executionResult.logs.join('\n')}
                              </pre>
                            </div>
                          )}

                          <details>
                            <summary className="text-xs font-medium cursor-pointer">Return Value</summary>
                            <pre className="mt-1 rounded bg-muted p-2 text-xs overflow-auto max-h-32">
                              {JSON.stringify(executionResult.output, null, 2)}
                            </pre>
                          </details>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Execution History */}
                {showHistory && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <History className="h-4 w-4" /> Execution History
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {executions.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">No executions yet</p>
                      ) : (
                        <div className="space-y-1">
                          {executions.map((ex) => (
                            <div
                              key={ex.id}
                              className={cn(
                                'flex items-center justify-between rounded px-3 py-2 text-xs',
                                ex.status === 'success' && 'bg-green-50 dark:bg-green-950/20',
                                ex.status === 'failed' && 'bg-red-50 dark:bg-red-950/20',
                                ex.status === 'timeout' && 'bg-yellow-50 dark:bg-yellow-950/20',
                              )}
                            >
                              <div className="flex items-center gap-2">
                                {ex.status === 'success' ? (
                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                ) : (
                                  <XCircle className="h-3 w-3 text-red-500" />
                                )}
                                <Badge variant="outline" className="text-[10px]">{ex.status}</Badge>
                                <span className="text-muted-foreground">{ex.triggered_by}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span>{ex.duration_ms}ms</span>
                                <span className="text-muted-foreground">
                                  {new Date(ex.created_at).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
                Select a script to view and edit
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Default Template ─────────────────────────────

const DEFAULT_SCRIPT = `// Tenant script — available variables: input, tenantId
// Helpers: log(), now(), uuid(), jsonParse(), jsonStringify()

log('Script started at', now());
log('Tenant:', tenantId);
log('Input:', jsonStringify(input));

// Your logic here:
const result = {
  processed: true,
  timestamp: now(),
  inputKeys: Object.keys(input),
};

log('Done!');
return result;`;
